import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useResizeObserver2 } from '@genially/ui/build/hooks/useResizeObserver2';
import { useEffectOnce } from '@genially/ui/src/hooks/useEffectOnce';

import { useLogRocketService } from '../../../../../../bootstrap/application/components/app/Routes/hooks/useLogRocketService';
import { ReactionsOverlay } from '../../../../../../shared/application/components/ReactionsOverlay/ReactionsOverlay';
import { useSnowplowService } from '../../../../../../shared/application/hooks/useSnowplow';
import { PlayerGeniallyView } from '../shared/PlayerGeniallyView/PlayerGeniallyView';
import { GameControlHeader } from './components/GameControlHeader/GameControlHeader';
import { HostCursorDot } from './components/HostCursorDot/HostCursorDot';
import { PlayerPlaySidebarSummary } from './components/PlayerPlaySidebarSummary/PlayerPlaySidebarSummary';
import { ReactionBar } from './components/ReactionBar/ReactionBar';
import { PlayerGameProvider, usePlayerGameContext } from './PlayerGame.context';
import {
  ContentWrapper,
  GameContentWrapper,
  GameControlHeaderWrapper,
  Wrapper,
} from './PlayerGame.styled';

const PlayerGameContent = observer(() => {
  const { reactions, hostCursorPosition } = usePlayerGameContext();

  const { setRef: containerRef, elementRect: containerRect } = useResizeObserver2();

  return (
    <GameContentWrapper>
      <ReactionsOverlay reactions={reactions} />

      <GameControlHeaderWrapper>
        <GameControlHeader />
      </GameControlHeaderWrapper>

      <ContentWrapper ref={containerRef}>
        <PlayerGeniallyView />
        <HostCursorDot
          xRatio={hostCursorPosition?.xRatio ?? 0}
          yRatio={hostCursorPosition?.yRatio ?? 0}
          visible={hostCursorPosition !== null}
          containerWidth={containerRect?.width ?? 0}
          containerHeight={containerRect?.height ?? 0}
          slideAspectRatio={hostCursorPosition?.slideAspectRatio ?? 1}
        />
      </ContentWrapper>
      <PlayerPlaySidebarSummary />
      <ReactionBar />
    </GameContentWrapper>
  );
});

interface PlayerGameProps {
  isCountdownHidden: boolean;
}

export const PlayerGame = observer(({ isCountdownHidden }: PlayerGameProps) => {
  const { trackWithSampling } = useLogRocketService();

  const { sendPlayerSessionJoinedEvent } = useSnowplowService();

  useEffectOnce(() => {
    sendPlayerSessionJoinedEvent('in-game');
  });

  useEffect(() => {
    trackWithSampling('play_participant', 10);
  }, [trackWithSampling]);

  return (
    <Wrapper>
      <PlayerGameProvider isCountdownHidden={isCountdownHidden}>
        <PlayerGameContent />
      </PlayerGameProvider>
    </Wrapper>
  );
});
